import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useFetch, usePromise } from '@rad/hooks';
import { getJson } from '@rad/utility/http';
import { requireToken } from '../../../utility/general';
import { clientToConversationSeed } from '../../../http/crm/conversations';
import CModal from '../modal';
const fullName = (c) => `${c.firstname} ${c.lastname}`.trim() || '(unnamed)';
const initialsFor = (c) => `${(c.firstname || '').charAt(0)}${(c.lastname || '').charAt(0)}`.toUpperCase() || '?';
const matches = (c, q) => {
    if (q.length === 0) {
        return true;
    }
    const needle = q.toLowerCase();
    if (fullName(c).toLowerCase().includes(needle)) {
        return true;
    }
    // Digit-only match against any phone on file
    const digits = q.replace(/\D/g, '');
    if (digits.length >= 3) {
        return c.contacts.some((x) => x.kind === 'phone' && x.value.replace(/\D/g, '').includes(digits));
    }
    return false;
};
const Avatar = (props) => _jsx("div", { className: "rounded-circle bg-primary text-white d-flex align-items-center justify-content-center flex-shrink-0", style: { 'width': '2.25rem', 'height': '2.25rem', 'fontWeight': 600, 'fontSize': '0.85rem' }, children: props.initials });
export default (props) => {
    const token = requireToken('merchant');
    const [clientsP] = useFetch(getJson(`/merchants/${token.merchant.id}/clients`));
    const clients = usePromise(clientsP) || [];
    const [filter, setFilter] = React.useState('');
    const [selectedId, setSelectedId] = React.useState(null);
    const filterInputRef = React.useRef(null);
    React.useEffect(() => {
        if (props.visible) {
            setFilter('');
            setSelectedId(null);
            // Focus the search on open
            setTimeout(() => { var _a; return (_a = filterInputRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 50);
        }
    }, [props.visible]);
    const filtered = React.useMemo(() => clients
        .filter((c) => matches(c, filter))
        .sort((a, b) => fullName(a).localeCompare(fullName(b)))
        .slice(0, 100), [clients, filter]);
    const selected = selectedId ? clients.find((c) => c.id === selectedId) || null : null;
    const selectedSeed = selected ? clientToConversationSeed(selected) : null;
    const canSubmit = !!selectedSeed;
    const handleSubmit = () => {
        if (!selectedSeed) {
            return;
        }
        props.onPick(selectedSeed);
    };
    return _jsxs(CModal, { visible: props.visible, title: "New Message", onClose: props.onClose, onSubmit: (done) => { handleSubmit(); done(); }, action: "Start Conversation", disabled: !canSubmit, children: [_jsx("p", { className: "text-muted small mb-2", children: "Pick a customer to text. We will use their mobile number on file." }), _jsx("input", { ref: filterInputRef, type: "text", className: "form-control mb-2", placeholder: "Search by name or phone...", value: filter, onChange: (e) => setFilter(e.currentTarget.value) }), _jsxs("div", { className: "border rounded", style: { 'maxHeight': '22rem', 'overflowY': 'auto' }, children: [clients.length === 0 && (_jsx("div", { className: "p-3 text-muted text-center small", children: "Loading customers..." })), clients.length > 0 && filtered.length === 0 && (_jsx("div", { className: "p-3 text-muted text-center small", children: "No customers match." })), filtered.map((c) => {
                        const seed = clientToConversationSeed(c);
                        const isSelected = c.id === selectedId;
                        const disabled = !seed;
                        return _jsxs("div", { className: `d-flex align-items-center gap-2 p-2 border-bottom ${isSelected ? 'bg-primary-subtle' : ''} ${disabled ? 'text-muted' : ''}`, style: { 'cursor': disabled ? 'not-allowed' : 'pointer', 'opacity': disabled ? 0.6 : 1 }, onClick: () => { if (!disabled) {
                                setSelectedId(c.id);
                            } }, children: [_jsx(Avatar, { initials: initialsFor(c) }), _jsxs("div", { className: "flex-grow-1 min-width-0", children: [_jsx("div", { className: "text-truncate", children: _jsx("strong", { children: fullName(c) }) }), _jsx("div", { className: "text-muted small", children: seed ? seed.customer_phone : 'No phone on file' })] })] }, c.id);
                    })] }), !!selectedSeed && (_jsxs("div", { className: "small text-muted mt-2", children: ["Texting ", _jsx("strong", { children: selectedSeed.customer_phone })] }))] });
};
